/**
 * API Call Tracker for Holiday Optimization
 * 
 * This utility helps track and limit API calls to prevent excessive usage
 */

class APICallTracker {
  constructor() {
    this.calls = new Map(); // endpoint -> { count, lastCall, requests: [] }
    this.maxCallsPerMinute = 3; // Limit to 3 calls per minute per endpoint
  }

  /**
   * Check if an API call should be allowed
   */
  shouldAllowCall(endpoint) {
    const now = Date.now();
    const oneMinute = 60 * 1000;
    
    if (!this.calls.has(endpoint)) {
      this.calls.set(endpoint, {
        count: 0,
        lastCall: 0,
        requests: []
      });
    }
    
    const callData = this.calls.get(endpoint);
    
    // Remove calls older than 1 minute
    callData.requests = callData.requests.filter(time => now - time < oneMinute);
    
    // Check if we're within the limit
    if (callData.requests.length >= this.maxCallsPerMinute) {
      console.warn(`🚫 API call blocked for ${endpoint}. Too many calls (${callData.requests.length}/${this.maxCallsPerMinute}) in the last minute.`);
      return false;
    }
    
    return true;
  }

  /**
   * Record an API call
   */
  recordCall(endpoint) {
    const now = Date.now();
    
    if (!this.calls.has(endpoint)) {
      this.calls.set(endpoint, {
        count: 0,
        lastCall: 0,
        requests: []
      });
    }
    
    const callData = this.calls.get(endpoint);
    callData.count++;
    callData.lastCall = now;
    callData.requests.push(now);
    
    console.log(`📊 API call recorded for ${endpoint}. Total calls: ${callData.count}, Recent calls: ${callData.requests.length}`);
  }

  /**
   * Get statistics for all tracked endpoints
   */
  getStats() {
    const stats = {};
    const now = Date.now();
    const oneMinute = 60 * 1000;
    
    for (const [endpoint, data] of this.calls.entries()) {
      const recentCalls = data.requests.filter(time => now - time < oneMinute);
      stats[endpoint] = {
        totalCalls: data.count,
        recentCalls: recentCalls.length,
        lastCall: data.lastCall ? new Date(data.lastCall).toISOString() : null
      };
    }
    
    return stats;
  }

  /**
   * Reset all tracking data
   */
  reset() {
    this.calls.clear();
    console.log('🔄 API call tracker reset');
  }
}

// Create a singleton instance
const apiCallTracker = new APICallTracker();

export default apiCallTracker;